import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { rutClean, rutValidate } from 'rut-helpers';
import { RequestGenerateSendOtp } from '../interfaces/generate-send-otp.interface';
import { GtmExtras } from '../interfaces/gtm.interface';
import { Patterns, RUT_PJ_THRESHOLD, SEQUENTIAL_NUMBER } from './constants';
import { NotificationType } from './enums';
import { SalesLevelModel } from '../interfaces/initial-parameters.interface';
import { CustomerDataUI } from '../interfaces/customer-data.interface';

export function buildRequestGenerateOtp(
	rut: string,
	newDestination?: string
): RequestGenerateSendOtp {
	return {
		rutCliente: rut,
		tipoNotificacion: NotificationType.DEFAULT,
		...(newDestination && { nuevoDestino: newDestination }),
	};
}

export function buildUrlSimulator(url: string, uuid: string, id: string): string {
	return `${url}?token=${uuid}&id=${id}`;
}

export function getRandomId() {
	return `${Date.now()}${Math.floor(Math.random() * 1000)}`;
}

export function pluralizeAttempts(value: string): string {
	return value === '1' ? `Te queda ${value} intento.` : `Te quedan ${value} intentos.`;
}

export function timerFormat(totalSeconds: number): string {
	const hrs = Math.floor(totalSeconds / 3600);
	const min = Math.floor((totalSeconds % 3600) / 60);
	const sec = totalSeconds % 60;

	return hrs > 0
		? `${hrs}:${min.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`
		: `${min}:${sec.toString().padStart(2, '0')}`;
}

export function isLegalEntity(rut: string): boolean {
	const rutNumber = Number(rutClean(rut).slice(0, -1));
	return rutNumber > RUT_PJ_THRESHOLD;
}

export function rutValidator(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const rut = control.value;
		if (!rut) return null;
		return rutValidate(rut) ? null : { invalidRut: true };
	};
}

export function naturalPersonValidator(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const rut = control.value;
		if (!rut) return null;
		return isLegalEntity(rut) ? { notNaturalPerson: true } : null;
	};
}

export function emailRegexValidator(): ValidatorFn {
	const regex = Patterns.email;
	return (control: AbstractControl): ValidationErrors | null => {
		const email = control.value;
		if (!email) return null;
		return regex.test(email) ? null : { invalidEmail: true };
	};
}

export function differentPhoneValidator(currentPhone: string): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		if (!control.value) return null;
		return control.value === currentPhone ? { samePhone: true } : null;
	};
}

export function findSelectedSalesRange(
	salesLevels: SalesLevelModel[],
	selectedLevel: string
): string {
	return salesLevels.find(item => item.level === selectedLevel)?.range ?? '';
}

export function ensureUserRut(customerData: CustomerDataUI) {
	return {
		...customerData,
		userRut: customerData.userRut || customerData.companyRut,
	};
}

export async function mapToGtmExtras(
	rut: string,
	email: string,
	level: string
): Promise<Partial<GtmExtras>> {
	return {
		'rut-empresa': convertToBase64(rut),
		sha256_mail_cta_digital: await sha256(email),
		monto: level,
	};
}

function convertToBase64(value: string): string {
	const bytes = new TextEncoder().encode(value);
	let binary = '';
	for (const byte of bytes) {
		binary += String.fromCharCode(byte);
	}
	return window.btoa(binary);
}

async function sha256(value: string): Promise<string> {
	const data = new TextEncoder().encode(value.trim().toLowerCase());
	const hashBuffer = await crypto.subtle.digest('SHA-256', data);

	return Array.from(new Uint8Array(hashBuffer))
		.map(b => b.toString(16).padStart(2, '0'))
		.join('');
}

export function hasSameRepeatedDigit(value: string): boolean {
	return Patterns.repeatDigits.test(value);
}

export function isSequentialNumber(value: string): boolean {
	return SEQUENTIAL_NUMBER.ASCENDING.includes(value) || SEQUENTIAL_NUMBER.DESCENDING.includes(value);
}

export function hasRepeatedTwoDigitPattern(value: string): boolean {
	return Patterns.twoDigitsRepeated.test(value);
}

export function phoneValidationsRegex(): ValidatorFn {
	return (control: AbstractControl<string | null>): ValidationErrors | null => {
		const value = control.value;
		if (!value) return null;

		const phoneNumber = value.slice(-8);
		const isInvalid =
			hasSameRepeatedDigit(phoneNumber) ||
			isSequentialNumber(phoneNumber) ||
			hasRepeatedTwoDigitPattern(phoneNumber);

		return isInvalid ? { invalidPhone: true } : null;
	};
}
